import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Navigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { TrainingCard, TrainingCardRef } from '@/features/calendar/components/TrainingCard';
import { AddTrainingModal } from '@/features/calendar/components/AddTrainingModal';
import { AddClassModal } from '@/features/calendar/components/AddClassModal';
import { DeleteClassModal } from '@/features/calendar/components/DeleteClassModal';
import { TrainingNotesModal } from '@/features/calendar/components/TrainingNotesModal';
import { TrainingViewModal } from '@/features/calendar/components/TrainingViewModal';


import { TrainingFilterModal, TrainingFilters } from '@/features/calendar/components/TrainingFilterModal';
import { MonthSlider } from '@/features/calendar/components/MonthSlider';
import { MyNotesTab } from '@/features/calendar/components/MyNotesTab';
import { useTrainingsWithNotes, useClassesWithNotes, useUpdateTrainingStatus, useUpdateTrainingNotes } from '@/features/calendar/hooks/useTrainings';
import { useInstructor } from '@/features/profile/hooks/useInstructor';
import { format, addMonths, subMonths, isSameDay, startOfMonth, endOfMonth, eachDayOfInterval, differenceInDays } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Training } from '@/types/trainings';
import { ArrowLeft, ArrowRight, Plus, Filter } from 'lucide-react';
import { TopNavigation } from '@/components/layout/TopNavigation';
import { BottomNavigation } from '@/components/layout/BottomNavigation';
import { useQueryClient, useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useI18n } from '@/contexts/I18nContext';
import { useLocation, useSearchParams } from 'react-router-dom';

export default function Calendar() {
  const { user, loading } = useAuth();
  const { isInstructor, isLoading: instructorLoading } = useInstructor();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [activeTab, setActiveTab] = useState<'classes' | 'trainings' | 'notes'>('classes');
  const [isAddTrainingModalOpen, setIsAddTrainingModalOpen] = useState(false);
  const [isAddClassModalOpen, setIsAddClassModalOpen] = useState(false);
  const [isEditClassModalOpen, setIsEditClassModalOpen] = useState(false);
  const [selectedClassForEdit, setSelectedClassForEdit] = useState<Training | null>(null);
  const [isDeleteClassModalOpen, setIsDeleteClassModalOpen] = useState(false);
  const [selectedClassForDelete, setSelectedClassForDelete] = useState<Training | null>(null);
  const [isNotesModalOpen, setIsNotesModalOpen] = useState(false);
  const [selectedTrainingForNotes, setSelectedTrainingForNotes] = useState<Training | null>(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [selectedTrainingForView, setSelectedTrainingForView] = useState<Training | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedTrainingForEdit, setSelectedTrainingForEdit] = useState<Training | null>(null);
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [showFixedButton, setShowFixedButton] = useState(false);
  const [lastUpdatedTrainingId, setLastUpdatedTrainingId] = useState<string | null>(null);
  const [lastCreatedClassId, setLastCreatedClassId] = useState<string | null>(null);
  const [notesRefreshKey, setNotesRefreshKey] = useState(0);
  const [updatingNotesTrainingId, setUpdatingNotesTrainingId] = useState<string | null>(null);
  const [activeFilters, setActiveFilters] = useState<TrainingFilters>({
    types: [],
    modalities: [],
    startDate: null,
    endDate: null,
    instructor: null,
    statuses: [],
    techniques: [],
    categories: []
  });
  const currentDayRef = useRef<HTMLDivElement>(null);
  const nearestTrainingRef = useRef<HTMLDivElement>(null);
  const actionBarRef = useRef<HTMLDivElement>(null);
  const monthNavigationRef = useRef<HTMLDivElement>(null);
  const trainingCardRefs = useRef<Map<string, TrainingCardRef>>(new Map());
  const viewModalRef = useRef<{ forceReload: () => Promise<void>; forceReloadInstructor: () => Promise<void> } | null>(null);
  const editModalRef = useRef<{ forceReload: () => Promise<void> } | null>(null);
  const [viewModalRefreshKey, setViewModalRefreshKey] = useState(0);

  const selectedMonth = selectedDate.getMonth() + 1;
  const selectedYear = selectedDate.getFullYear();

  const queryClient = useQueryClient();
  const { t, currentLanguage } = useI18n();

  // Track if we came from a notification to prevent auto-scroll
  const fromNotificationRef = useRef(false);
  const notificationTrainingIdRef = useRef<string | null>(null);

  // Set default tab based on user type (or navigation state)
  const hasSetInitialTabRef = useRef(false);

  useEffect(() => {
    if (!instructorLoading && !hasSetInitialTabRef.current && !notificationTrainingIdRef.current) {
      // Allow deep-linking to a specific tab via navigation state
      const requestedTab = location.state?.activeTab as 'classes' | 'trainings' | 'notes' | undefined;
      if (requestedTab) {
        setActiveTab(requestedTab);
      } else {
        setActiveTab(isInstructor ? 'classes' : 'trainings');
      }
      hasSetInitialTabRef.current = true;
    }
  }, [isInstructor, instructorLoading, location.state?.activeTab]);

  // Check for navigation state to open Add Class modal
  useEffect(() => {
    if (location.state?.openAddClassModal && isInstructor) {
      setIsAddClassModalOpen(true);
      setActiveTab('classes');
      // Clear the state to prevent reopening on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location.state, isInstructor]);

  // Check for navigation state to open Add Training modal
  useEffect(() => {
    if (location.state?.openAddTrainingModal) {
      setIsAddTrainingModalOpen(true);
      setActiveTab('trainings');
      // Clear the state to prevent reopening on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  // Use different hooks based on active tab and user type
  const { data: trainingsData, isLoading: trainingsLoading, refetch: refetchTrainings } = useTrainingsWithNotes({
    month: selectedMonth,
    year: selectedYear
  });

  // Get ALL trainings for monthly totals calculation (direct query, not RPC)
  // This ensures the MonthSlider shows counts for all months, not just the selected one.
  // Fetches ALL records where user_id = user.id — both personal trainings (is_class=false)
  // AND classes attended as a student (is_class=true, created by an instructor).
  // The instructor "My Trainings" tab filters is_class=false in memory inside calculateMonthlyCounts.
  const { data: allTrainingsForTotals } = useQuery({
    queryKey: ['all-trainings-for-totals', user?.id],
    queryFn: async () => {
      if (!user?.id) return [];

      const { data, error } = await supabase
        .from('trainings')
        .select('id, start_time, status, is_class')
        .eq('user_id', user.id) // all records where this user is the student/participant
        .order('start_time', { ascending: false });

      if (error) throw error;
      return data || [];
    },
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });

  // Get ALL instructor classes for monthly totals calculation
  // Classes have instructor_id = user.id (not user_id), so we need a separate query
  // This feeds the MonthSlider on the "My Classes" tab for instructors
  const { data: allInstructorClassesForTotals } = useQuery({
    queryKey: ['all-instructor-classes-for-totals', user?.id],
    queryFn: async () => {
      if (!user?.id) return [];

      const { data, error } = await supabase
        .from('trainings')
        .select('id, start_time, status, is_class')
        .eq('instructor_id', user.id)
        .eq('is_class', true) // Only classes (instructor role)
        .order('start_time', { ascending: false });

      if (error) throw error;
      return data || [];
    },
    enabled: !!user?.id && isInstructor,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });

  // For instructor classes tab: fetch all classes where instructor_id = me for the selected month.
  // Querying by instructor_id (not user_id) because these are records where the user is the TEACHER.
  // selectedMonth/selectedYear in the queryKey ensures React Query auto-refetches when the month changes.
  const { data: instructorClassesForMonth = [], isLoading: instructorClassesLoading, refetch: refetchInstructorClasses } = useQuery<Training[]>({
    queryKey: ['instructor-classes-for-month', user?.id, selectedMonth, selectedYear],
    queryFn: async () => {
      if (!user?.id) return [];
      const startOfSelectedMonth = new Date(selectedYear, selectedMonth - 1, 1);
      const endOfSelectedMonth = new Date(selectedYear, selectedMonth, 0, 23, 59, 59, 999);
      const { data, error } = await supabase
        .from('trainings')
        .select('*')
        .eq('instructor_id', user.id)
        .eq('is_class', true)
        .gte('start_time', startOfSelectedMonth.toISOString())
        .lte('start_time', endOfSelectedMonth.toISOString())
        .order('start_time', { ascending: true });
      if (error) throw error;
      return (data as Training[]) || [];
    },
    enabled: !!user?.id && isInstructor,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  // Check for navigation state or URL params to open Training View modal (from notifications)
  useEffect(() => {
    // Support both React Router state (in-app nav) and URL query params (push notification click)
    const trainingId = location.state?.trainingId || searchParams.get('trainingId');
    const shouldOpenModal = location.state?.openTrainingModal || !!searchParams.get('trainingId');

    if (shouldOpenModal && trainingId) {
      fromNotificationRef.current = true;
      notificationTrainingIdRef.current = trainingId;

      if (!isInstructor) {
        setActiveTab('trainings');
      }

      window.scrollTo(0, 0);

      const highlightDate = location.state?.highlightDate;
      if (highlightDate) {
        setSelectedDate(new Date(highlightDate));
      }
    }
  }, [location.state, searchParams, isInstructor]);

  // Find and open the training modal when data is available
  useEffect(() => {
    if (notificationTrainingIdRef.current) {
      const trainingId = notificationTrainingIdRef.current;
      // Find the training in the current data
      const allTrainings = trainingsData?.trainings || [];
      const training = allTrainings.find((t: Training) => t.id === trainingId) ||
                      instructorClassesForMonth.find((c: Training) => c.id === trainingId);
      
      if (training) {
        // Set the correct tab based on training type
        // For instructors: 'classes' if it's a class, 'trainings' if it's a training
        // For practitioners: always 'trainings'
        const tabToSet = isInstructor 
          ? (training.is_class ? 'classes' : 'trainings')
          : 'trainings';
        setActiveTab(tabToSet);
        // Then set the training and open modal
        setSelectedTrainingForView(training);
        // Small delay to ensure tab is set before opening modal
        setTimeout(() => {
          setIsViewModalOpen(true);
        }, 10);
        // Clear the ref so we don't try to open again
        notificationTrainingIdRef.current = null;
      }
    }
  }, [trainingsData, instructorClassesForMonth]);

  // Get classes notes data for instructor classes tab
  const { data: classesNotesData } = useClassesWithNotes({
    month: selectedMonth,
    year: selectedYear,
  });

  // Determine which data to use based on active tab
  const isClassesTab = isInstructor && activeTab === 'classes';

  // Tab data source:
  //   "Minhas Aulas" (isClassesTab):  instructor_id = me → instructorClassesForMonth
  //   "Meus Treinos" (instructor):    user_id = me, is_class=false only
  //   "Meus Treinos" (student):       user_id = me, all records (is_class=true = class attended as student)
  //
  // Mental model: instructor_id = me means I am TEACHING; user_id = me means I am the STUDENT.
  // For instructors, get_user_trainings_including_classes returns ALL records where user_id=me,
  // including is_class=true records they created as instructor (instructor_id=user_id=self).
  // Those must be excluded from "Meus Treinos" since they already appear in "Minhas Aulas".
  const trainings = isClassesTab
    ? instructorClassesForMonth
    : isInstructor
      ? (trainingsData?.trainings || []).filter(t => !t.is_class)
      : (trainingsData?.trainings || []);

  // Use appropriate notes data based on active tab
  const notesData = isClassesTab ? (classesNotesData?.notesData || {}) : (trainingsData?.notesData || {});

  // Merge techniques from notesData into trainings before filtering
  const trainingsWithTechniques = useMemo(() => {
    return trainings.map(training => {
      const trainingNotesData = notesData[training.id];
      const techniques = trainingNotesData?.techniques || [];
      return {
        ...training,
        techniques: techniques
      };
    });
  }, [trainings, notesData]);

  // Refetch trainings data when month/year changes.
  // instructor-classes-for-month auto-refetches because selectedMonth/selectedYear are in its queryKey.
  useEffect(() => {
    if (!isClassesTab) {
      refetchTrainings();
    }
  }, [selectedMonth, selectedYear, isClassesTab, refetchTrainings]);

  const hasActiveFilters = () => {
    return (
      activeFilters.types.length > 0 ||
      activeFilters.modalities.length > 0 ||
      activeFilters.startDate !== null ||
      activeFilters.endDate !== null ||
      activeFilters.instructor !== null ||
      activeFilters.statuses.length > 0 ||
      activeFilters.techniques.length > 0 ||
      activeFilters.categories.length > 0
    );
  };

  // Filter trainings based on active filters
  const filteredTrainings = useMemo(() => {
    if (!hasActiveFilters()) {
      return trainingsWithTechniques.sort((a, b) => new Date(a.start_time).getTime() - new Date(b.start_time).getTime());
    }

    return trainingsWithTechniques.filter(training => {
      // Filter by training type
      // Map translated types back to database values for comparison
      if (activeFilters.types.length > 0) {
        const typeMap: Record<string, string> = {
          [t('calendar.group_class')]: 'Group Class',
          [t('calendar.private_class')]: 'Private Class',
          // Also handle English values in case they're used
          'Group Class': 'Group Class',
          'Private Class': 'Private Class',
        };
        
        const mappedTypes = activeFilters.types.map(t => typeMap[t] || t);
        if (!mappedTypes.includes(training.type || '')) {
          return false;
        }
      }

      // Filter by modality
      if (activeFilters.modalities.length > 0) {
        if (!activeFilters.modalities.includes(training.modality)) {
          return false;
        }
      }

      // Filter by category
      if (activeFilters.categories.length > 0) {
        if (!training.category || !activeFilters.categories.includes(training.category)) {
          return false;
        }
      }

      // Filter by status
      // Map translated status back to database value for comparison
      if (activeFilters.statuses.length > 0) {
        const statusMap: Record<string, string> = {
          [t('calendar.completed')]: 'Completed',
          [t('calendar.missed')]: 'Missed',
          [t('calendar.scheduled')]: 'Scheduled',
          // Also handle English values in case they're used
          'Completed': 'Completed',
          'Missed': 'Missed',
          'Scheduled': 'Scheduled',
        };
        
        const mappedStatuses = activeFilters.statuses.map(s => statusMap[s] || s);
        if (!mappedStatuses.includes(training.status)) {
          return false;
        }
      }

      // Filter by date range
      if (activeFilters.startDate) {
        const trainingDate = new Date(training.start_time);
        const startDate = typeof activeFilters.startDate === 'string' 
          ? activeFilters.startDate 
          : activeFilters.startDate.toISOString().split('T')[0];
        
        // Compare date strings directly to avoid timezone issues
        const trainingDateStr = trainingDate.toISOString().split('T')[0];
        
        // Include trainings on or after the start date
        if (trainingDateStr < startDate) {
          return false;
        }
      }

      if (activeFilters.endDate) {
        const trainingDate = new Date(training.start_time);
        const endDate = typeof activeFilters.endDate === 'string' 
          ? activeFilters.endDate 
          : activeFilters.endDate.toISOString().split('T')[0];
        
        // Compare date strings directly to avoid timezone issues
        const trainingDateStr = trainingDate.toISOString().split('T')[0];
        
        // Include trainings on or before the end date
        if (trainingDateStr > endDate) {
          return false;
        }
      }

      // Filter by instructor
      if (activeFilters.instructor) {
        if (training.instructor_id !== activeFilters.instructor.id) {
          return false;
        }
      }

      // Filter by techniques
      if (activeFilters.techniques.length > 0) {
        const trainingTechniques = training.techniques || [];
        const hasMatchingTechnique = activeFilters.techniques.some(techniqueFilter => 
          trainingTechniques.some((t: any) => 
            // Compare with both name and name_br to handle i18n
            t.name === techniqueFilter || 
            t.name_br === techniqueFilter
          )
        );
        if (!hasMatchingTechnique) {
          return false;
        }
      }

      return true;
    }).sort((a, b) => new Date(a.start_time).getTime() - new Date(b.start_time).getTime());
  }, [trainingsWithTechniques, activeFilters, t]);
  

  

  


  // Calculate monthly counts from actual trainings/classes data, filtering by Completed status
  // This ensures the monthly slider shows only completed sessions, not all trainings
  // IMPORTANT: Use allTrainingsDataForTotals (all months) for slider, not trainingsData (selected month only)
  const calculateMonthlyCounts = useMemo(() => {
    const currentYear = new Date().getFullYear();
    
    // Helper to count completed items by month
    // CRITICAL: Always returns exactly 12 items, one for each month (1-12), even if count is 0
    const countByMonth = (items: any[], year: number): Array<{ month: number; completed_count: number }> => {
      const counts: Record<number, number> = {};
      items
        .filter(item => item.status === 'Completed')
        .forEach(item => {
          const itemDate = new Date(item.start_time);
          if (itemDate.getFullYear() === year) {
            const month = itemDate.getMonth() + 1;
            counts[month] = (counts[month] || 0) + 1;
          }
        });
      
      // Fill all 12 months - ALWAYS return 12 items in order (1-12), even if no data
      return Array.from({ length: 12 }, (_, i) => {
        const month = i + 1;
        return { month, completed_count: counts[month] || 0 };
      });
    };
    
    if (isClassesTab) {
      // "Minhas Aulas": count classes where I am the instructor (instructor_id = me, all months)
      const allClasses = allInstructorClassesForTotals || [];
      const prev = countByMonth(allClasses, currentYear - 1);
      const current = countByMonth(allClasses, currentYear);
      const next = countByMonth(allClasses, currentYear + 1);
      // Always returns 36 items: [prev year months 1-12, current year months 1-12, next year months 1-12]
      return [...prev, ...current, ...next];
    } else if (activeTab === 'trainings' && isInstructor) {
      // For instructors on "My Trainings" tab: count only personal trainings (is_class=false).
      // allTrainingsForTotals includes both personal trainings AND attended classes (is_class=true),
      // so we must filter out classes here to avoid mixing instructor and student counts.
      const allTrainings = (allTrainingsForTotals || []).filter(t => !t.is_class);
      const prev = countByMonth(allTrainings, currentYear - 1);
      const current = countByMonth(allTrainings, currentYear);
      const next = countByMonth(allTrainings, currentYear + 1);
      // Always returns 36 items: [prev year months 1-12, current year months 1-12, next year months 1-12]
      return [...prev, ...current, ...next];
    } else {
      // "Meus Treinos" (instructor) and student: all records where user_id = me.
      // Includes personal trainings (is_class=false) AND classes attended as a student (is_class=true).
      // No is_class filtering — if user_id = me, it belongs in "Meus Treinos".
      const allTrainings = allTrainingsForTotals || [];
      const prev = countByMonth(allTrainings, currentYear - 1);
      const current = countByMonth(allTrainings, currentYear);
      const next = countByMonth(allTrainings, currentYear + 1);
      return [...prev, ...current, ...next];
    }
  }, [isClassesTab, activeTab, isInstructor, allTrainingsForTotals, allInstructorClassesForTotals]);
  
  // Use the calculated monthly counts for the slider
  const monthlyData = calculateMonthlyCounts;

  const updateStatusMutation = useUpdateTrainingStatus();
  const updateNotesMutation = useUpdateTrainingNotes();

  // Redirect if not authenticated
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#160C0C] text-white">
        <div className="text-lg font-medium">Loading calendar...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/" replace />;
  }

  const handleMonthChange = (direction: 'next' | 'prev') => {
    setSelectedDate(prev => 
      direction === 'next' ? addMonths(prev, 1) : subMonths(prev, 1)
    );
  };

  const handleStatusUpdate = async (trainingId: string, status: string) => {
    try {
      await updateStatusMutation.mutateAsync({ 
        training_id: trainingId, 
        status: status as 'Completed' | 'Missed' 
      });

      // Invalidate and refetch data
      await queryClient.invalidateQueries({ queryKey: ['trainings'] });
      await queryClient.invalidateQueries({ queryKey: ['trainings-with-notes'] });
      await queryClient.invalidateQueries({ queryKey: ['monthly-totals'] });
      await queryClient.invalidateQueries({ queryKey: ['next-trainings'] });
      await queryClient.invalidateQueries({ queryKey: ['journey-highlights'] });
      await queryClient.invalidateQueries({ queryKey: ['all-trainings-for-totals'] });

      // Refetch instructor classes if user is instructor
      if (isInstructor) {
        await queryClient.invalidateQueries({ queryKey: ['instructor-classes'] });
        await queryClient.invalidateQueries({ queryKey: ['instructor-classes', user?.id] });
        await queryClient.invalidateQueries({ queryKey: ['instructor-classes-for-month'], exact: false });
        await queryClient.invalidateQueries({ queryKey: ['all-instructor-classes-for-totals'] });
      }

      // Show success message
      } catch (error) {
        // Silent fail
      console.log('Failed to update training status');
    }
  };

  const handleNotesUpdate = async (trainingId: string, notes: string) => {
    // If notes is empty string, it means we want to open the notes modal
    if (notes === '') {
      const training = trainings.find(t => t.id === trainingId);
      if (training) {
        handleOpenNotesModal(training);
      }
      return;
    }
    
    setUpdatingNotesTrainingId(trainingId);
    try {
      await updateNotesMutation.mutateAsync({ training_id: trainingId, notes });
      
      // Force refresh of all data to update the UI
      await queryClient.invalidateQueries({ queryKey: ['trainings-with-notes'] });
      const result = await refetchTrainings();
      
      // Force a re-render of all TrainingCard components to update their notes status
      setNotesRefreshKey(prev => prev + 1);
      
    } catch (error) {
      console.error('Error updating training notes:', error);
      throw error;
    } finally {
      setUpdatingNotesTrainingId(null);
    }
  };

  const handleTrainingCreated = async () => {
    // Force invalidate and refetch trainings to get the new training
    await queryClient.invalidateQueries({ queryKey: ['trainings-with-notes'] });
    await queryClient.invalidateQueries({ queryKey: ['trainings'] });
    await queryClient.invalidateQueries({ queryKey: ['monthly-totals'] });
    await queryClient.invalidateQueries({ queryKey: ['next-trainings'] });
    await queryClient.invalidateQueries({ queryKey: ['journey-highlights'] });
    
    // Also invalidate the specific query key with current month/year
    await queryClient.invalidateQueries({ 
      queryKey: ['trainings-with-notes', user?.id, selectedMonth, selectedYear] 
    });
    
    // Refetch the current data
    await refetchTrainings();
    
    // Close the modal
    setIsAddTrainingModalOpen(false);
    
    // Show success message
  };

  const handleOpenNotesModal = (training: Training) => {
    setSelectedTrainingForNotes(training);
    setIsNotesModalOpen(true);
  };

  const handleOpenViewModal = (training: Training) => {
    setSelectedTrainingForView(training);
    setIsViewModalOpen(true);
  };

  const handleOpenEditModal = (training: Training) => {
    setSelectedTrainingForEdit(training);
    setIsEditModalOpen(true);
  };

  const handleTrainingUpdated = async () => {
    const wasEditModalOpen = isEditModalOpen;
    
    // Close modal immediately for better UX
    if (wasEditModalOpen) {
      setIsEditModalOpen(false);
      setSelectedTrainingForEdit(null);
    }
    
    // Invalidate and refetch in background (don't await to make it faster)
    // This ensures UI updates immediately while data refreshes
    queryClient.invalidateQueries({ queryKey: ['trainings-with-notes'], exact: false });
    queryClient.invalidateQueries({ queryKey: ['trainings'], exact: false });
    queryClient.invalidateQueries({ queryKey: ['monthly-totals'], exact: false });
    queryClient.invalidateQueries({ queryKey: ['next-trainings'], exact: false });
    
    // CRITICAL: Always refetch and update view modal if it's open
    // This ensures TrainingViewModal shows updated data even when editing from view modal
    const result = await refetchTrainings();
    
    // Update view modal if it's open (regardless of whether edit modal was open)
    const wasViewModalOpen = isViewModalOpen && selectedTrainingForView;
    if (wasViewModalOpen && result.data && result.data.trainings) {
      const updatedTraining = result.data.trainings.find(t => t.id === selectedTrainingForView.id);
      
      if (updatedTraining) {
        // CRITICAL: Create a completely new object to force React to detect the change
        // This ensures TrainingViewModal's useEffect detects the change in start_time/end_time
        const newTrainingObject: Training = {
          ...updatedTraining,
          // Explicitly copy all fields to ensure it's a new reference
          start_time: updatedTraining.start_time,
          end_time: updatedTraining.end_time,
        };
        
        setSelectedTrainingForView(newTrainingObject);
        
        // Force reload of instructor and partners data in the view modal
        if (viewModalRef.current) {
          await viewModalRef.current.forceReload();
          await viewModalRef.current.forceReloadInstructor();
        }
        
        // Force refresh by updating the key
        setViewModalRefreshKey(prev => prev + 1);
      }
    }

    // Update the selected training in the edit modal if it exists
    if (selectedTrainingForEdit && result.data && result.data.trainings) {
      const updatedTraining = result.data.trainings.find(t => t.id === selectedTrainingForEdit.id);
      
      if (updatedTraining) {
        // Force a complete re-render by creating a new object
        setSelectedTrainingForEdit({ ...updatedTraining });
        
        // Force multiple updates to ensure the modal refreshes immediately
        setTimeout(() => {
          setSelectedTrainingForEdit(prev => prev ? { ...prev } : prev);
        }, 50);
        
        setTimeout(() => {
          setSelectedTrainingForEdit(prev => prev ? { ...prev } : prev);
        }, 200);
      }
    }
  };

  const handleTrainingDeleted = () => {
    // Invalidate and refetch trainings to remove deleted training from the list
    queryClient.invalidateQueries({ queryKey: ['trainings-with-notes'] });
    queryClient.invalidateQueries({ queryKey: ['trainings'] });
    queryClient.invalidateQueries({ queryKey: ['monthly-totals'] });
    queryClient.invalidateQueries({ queryKey: ['monthly-trainings'] });
  };

  const handleNotesSaved = async (trainingId: string, notes: string) => {
    // If the view modal is open and showing the same training, force a re-render
    if (selectedTrainingForView && selectedTrainingForView.id === trainingId) {
      // Force a re-render by updating the selectedTrainingForView with a new object
      // This will trigger the useEffect in TrainingViewModal and refetch notes
      setSelectedTrainingForView({ ...selectedTrainingForView });
    }
  };

  const handleDataSaved = async () => {
    // Invalidate appropriate queries based on active tab
    if (isClassesTab) {
      await queryClient.invalidateQueries({ queryKey: ['classes-with-notes'] });
      await queryClient.invalidateQueries({ queryKey: ['instructor-classes'] });
      await queryClient.invalidateQueries({ queryKey: ['instructor-daily-totals'] });
      await queryClient.invalidateQueries({ queryKey: ['journey-highlights'] });
      // Force refetch of classes notes
      await queryClient.refetchQueries({ queryKey: ['classes-with-notes'] });
    } else {
      // CRITICAL: Invalidate with exact match to ensure the right query is invalidated
      // useTrainingsWithNotes uses queryKey: ['trainings-with-notes', user?.id, month, year]
      await queryClient.invalidateQueries({ 
        queryKey: ['trainings-with-notes'],
        exact: false // Match all variations (different months/years)
      });
      
      // Force refetch with exact match - this ensures the current month/year query is refetched
      await queryClient.refetchQueries({ 
        queryKey: ['trainings-with-notes'],
        exact: false
      });
      
      // Also call the refetch function directly to ensure it happens
      const result = await refetchTrainings();
      
      // If the view modal is open, update it with fresh data
      if (selectedTrainingForView && result.data && result.data.trainings) {
        // Find the updated training in the fresh data
        const updatedTraining = result.data.trainings.find(t => t.id === selectedTrainingForView.id);
        if (updatedTraining) {
          setSelectedTrainingForView({ ...updatedTraining });
          
          // Force reload of the view modal
          if (viewModalRef.current) {
            await viewModalRef.current.forceReload();
          }
          
          // Force refresh of partners by updating the refresh key
          setViewModalRefreshKey(prev => prev + 1);
        }
      }
    }
  
    // Force a re-render of all TrainingCard components to update their notes status
    // This must happen after the queries are invalidated and refetched
    setNotesRefreshKey(prev => prev + 1);
  };

  const handleOpenFilterModal = () => {
    setIsFilterModalOpen(true);
  };

  const handleApplyFilters = (filters: TrainingFilters) => {
    setActiveFilters(filters);
  };

  const handleClearFilters = () => {
    const emptyFilters: TrainingFilters = {
      types: [],
      modalities: [],
      startDate: null,
      endDate: null,
      instructor: null,
      statuses: [],
      techniques: [],
      categories: []
    };
    setActiveFilters(emptyFilters);
  };

  // Instructor-specific handlers
  const handleAddClass = () => {
    setIsAddClassModalOpen(true);
  };

  const handleConfigureAgenda = () => {
    // TODO: Implement agenda configuration
    console.log('Configure agenda clicked');
  };

  const handleTabChange = async (tab: 'classes' | 'trainings' | 'notes') => {
    setActiveTab(tab);
    
    // CRITICAL: Force refetch when switching tabs to ensure fresh data
    // This is especially important when switching to "My Training Sessions" tab
    // to ensure classes don't appear from stale cache
    if (tab === 'trainings') {
      // When switching to trainings tab, force refetch to get fresh data
      // This ensures that if a class was just created, it won't appear in wrong tab
      await queryClient.invalidateQueries({ queryKey: ['trainings-with-notes'], exact: false });
      await refetchTrainings();
    } else if (tab === 'classes') {
      // When switching to classes tab, invalidate the monthly instructor classes query
      await queryClient.invalidateQueries({ queryKey: ['instructor-classes-for-month'], exact: false });
      await refetchInstructorClasses();
    } else if (tab === 'notes') {
      // When switching to notes tab, invalidate notes queries
      // But NOT the monthly-totals query, which should remain stable
      await queryClient.invalidateQueries({ 
        queryKey: ['user-notes'], 
        exact: false
      });
      // Note: We don't invalidate 'user-notes-monthly-totals' to keep it stable
    }
  };

  const handleClassCreated = async () => {
    // CRITICAL: Force invalidate and refetch ALL related queries
    // This ensures classes don't appear in wrong tabs due to stale cache
    // Especially important in dev environment where cache might persist
    await queryClient.invalidateQueries({ queryKey: ['instructor-classes'], exact: false });
    await queryClient.invalidateQueries({ queryKey: ['instructor-classes-for-month'], exact: false });
    await queryClient.invalidateQueries({ queryKey: ['trainings-with-notes'], exact: false });
    await queryClient.invalidateQueries({ queryKey: ['classes-with-notes'], exact: false });
    await queryClient.invalidateQueries({ queryKey: ['instructor-daily-totals'], exact: false });
    await queryClient.invalidateQueries({ queryKey: ['instructor-highlights'], exact: false });
    await queryClient.invalidateQueries({ queryKey: ['monthly-totals'], exact: false });
    await queryClient.invalidateQueries({ queryKey: ['monthly-classes'], exact: false });
    await queryClient.invalidateQueries({ queryKey: ['monthly-trainings'], exact: false });
    
    // CRITICAL: Invalidate student statistics when training/class is created
    // This ensures student home page statistics update when they create a new training
    await queryClient.invalidateQueries({ queryKey: ['practitioner-highlights'], exact: false });
    await queryClient.invalidateQueries({ queryKey: ['journey-highlights'], exact: false });
    
    // Also invalidate regular trainings since classes are stored in the same table
    await queryClient.invalidateQueries({ queryKey: ['trainings-with-notes'] });
    await queryClient.invalidateQueries({ queryKey: ['trainings'] });
    await queryClient.invalidateQueries({ queryKey: ['monthly-totals'] });
    
    // Refetch instructor classes if we're on the classes tab
    if (isInstructor && activeTab === 'classes') {
      await refetchInstructorClasses();
    }
    
    // Refetch the current data
    const result = await refetchTrainings();
    
    // Find the newly created class and scroll to it
    if (result.data && result.data.trainings) {
      // Find the most recently created class (assuming it's the last one in the array)
      const newClass = result.data.trainings[result.data.trainings.length - 1];
      if (newClass) {
        setLastCreatedClassId(newClass.id);
        
        // Trigger scroll after a delay to allow the UI to update
        setTimeout(() => {
          const cardRef = trainingCardRefs.current.get(newClass.id);
          if (cardRef) {
            cardRef.animateUpdate();
            // Check if scrollIntoView is available (not available in test environments)
            if (typeof cardRef.scrollIntoView === 'function') {
              cardRef.scrollIntoView();
            }
          }
        }, 300);
      }
    }
    
    // Close the modal
    setIsAddClassModalOpen(false);
    
    // Show success message
    console.log('Class created successfully');
  };

  const handleOpenEditClassModal = (training: Training) => {
    setSelectedClassForEdit(training);
    setIsEditClassModalOpen(true);
  };

  const handleClassUpdated = async () => {
    // Force invalidate and refetch instructor classes to get updated data
    await queryClient.invalidateQueries({ queryKey: ['instructor-classes'] });
    await queryClient.invalidateQueries({ queryKey: ['instructor-daily-totals'] });
    
    // Also invalidate regular trainings since classes are stored in the same table
    await queryClient.invalidateQueries({ queryKey: ['trainings-with-notes'] });
    const result = await refetchTrainings();
    
    // Update the selected class in the edit modal if it exists
    if (selectedClassForEdit && result.data && result.data.trainings) {
      const updatedClass = result.data.trainings.find(t => t.id === selectedClassForEdit.id);
      
      if (updatedClass) {
        // Force a complete re-render by creating a new object
        setSelectedClassForEdit({ ...updatedClass });
        
        // Force multiple updates to ensure the modal refreshes immediately
        setTimeout(() => {
          setSelectedClassForEdit(prev => prev ? { ...prev } : prev);
        }, 50);
        
        setTimeout(() => {
          setSelectedClassForEdit(prev => prev ? { ...prev } : prev);
        }, 200);
      }
    }
  };

  const handleClassDeleted = async (classId?: string) => {
    // If a specific class ID is provided, delete it
    if (classId) {
      try {
        const { error } = await supabase
          .from('trainings')
          .delete()
          .eq('id', classId);

        if (error) {
          throw error;
        }
      } catch (error) {
        console.error('Error deleting class:', error);
        return;
      }
    }

    // Invalidate and refetch classes to remove deleted class from the list
    await queryClient.invalidateQueries({ queryKey: ['instructor-classes'] });
    await queryClient.invalidateQueries({ queryKey: ['instructor-daily-totals'] });
    await queryClient.invalidateQueries({ queryKey: ['trainings'] });
    await queryClient.invalidateQueries({ queryKey: ['trainings-with-notes'] });
  };

  const handleOpenDeleteClassModal = (training: Training) => {
    setSelectedClassForDelete(training);
    setIsDeleteClassModalOpen(true);
  };

  const handleConfirmDeleteClass = async () => {
    if (!selectedClassForDelete) return;

    try {
      // Delete the class
      const { error } = await supabase
        .from('trainings')
        .delete()
        .eq('id', selectedClassForDelete.id);

      if (error) {
        throw error;
      }

      // Invalidate and refetch data
      await queryClient.invalidateQueries({ queryKey: ['instructor-classes'] });
      await queryClient.invalidateQueries({ queryKey: ['instructor-daily-totals'] });
      await queryClient.invalidateQueries({ queryKey: ['trainings'] });
      await queryClient.invalidateQueries({ queryKey: ['trainings-with-notes'] });

      // Close modal and reset state
      setIsDeleteClassModalOpen(false);
      setSelectedClassForDelete(null);

      // Show success message
      console.log('Class deleted successfully');
    } catch (error) {
      console.error('Error deleting class:', error);
    }
  };

  // Handle scroll to show/hide fixed button and month navigation
  useEffect(() => {
    const handleScroll = () => {
      if (actionBarRef.current && monthNavigationRef.current) {
        const actionBarRect = actionBarRef.current.getBoundingClientRect();
        const monthNavigationRect = monthNavigationRef.current.getBoundingClientRect();
        
        // Show fixed elements when either the action bar or month navigation is not visible
        const isActionBarVisible = actionBarRect.top > 0;
        const isMonthNavigationVisible = monthNavigationRect.top > 0;
        
        setShowFixedButton(!isActionBarVisible || !isMonthNavigationVisible);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Group trainings by day
  const trainingsByDay = useMemo(() => {
    const grouped = filteredTrainings.reduce((acc, training) => {
      const day = format(new Date(training.start_time), 'yyyy-MM-dd');
      if (!acc[day]) {
        acc[day] = [];
      }
      acc[day].push(training);
      return acc;
    }, {} as Record<string, typeof trainings>);

    // Sort trainings by start_time within each day
    Object.keys(grouped).forEach(day => {
      grouped[day].sort((a, b) => 
        new Date(a.start_time).getTime() - new Date(b.start_time).getTime()
      );
    });

    return grouped;
  }, [filteredTrainings]);

  // Get all days in the selected month
  const daysInMonth = useMemo(() => {
    const monthStart = startOfMonth(selectedDate);
    const monthEnd = endOfMonth(selectedDate);
    return eachDayOfInterval({ start: monthStart, end: monthEnd });
  }, [selectedDate]);



  // Find the nearest training to today
  const findNearestTraining = useCallback(() => {
    const today = new Date();
    let nearestTraining = null;
    let minDaysDiff = Infinity;

    filteredTrainings.forEach(training => {
      const trainingDate = new Date(training.start_time);
      const daysDiff = Math.abs(differenceInDays(trainingDate, today));
      
      if (daysDiff < minDaysDiff) {
        minDaysDiff = daysDiff;
        nearestTraining = training;
      }
    });

    return nearestTraining;
  }, [filteredTrainings]);

  // Auto-scroll to current day on mount (only when month changes, not when trainings update)
  // Skip auto-scroll if we came from a notification
  useEffect(() => {
    if (fromNotificationRef.current) {
      return; // Don't auto-scroll when coming from notification
    }
    
    if (currentDayRef.current && selectedMonth === new Date().getMonth() + 1 && selectedYear === new Date().getFullYear()) {
      setTimeout(() => {
        // Check if scrollIntoView is available (not available in test environments)
        if (currentDayRef.current && typeof currentDayRef.current.scrollIntoView === 'function') {
          currentDayRef.current.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'center' 
          });
        }
      }, 100);
    }
  }, [selectedMonth, selectedYear]); // Removed 'trainings' dependency

  // Removed auto-scroll to nearest training to avoid conflicts with status update scroll

  const isCurrentDay = useCallback((date: Date) => {
    return isSameDay(date, new Date());
  }, []);

  const nearestTraining = useMemo(() => findNearestTraining(), [findNearestTraining]);

  // Debug notesData for selected training
  useEffect(() => {
    if (selectedTrainingForNotes && notesData) {
      const trainingNotes = notesData[selectedTrainingForNotes.id];
      // Debug data removed for cleaner console
    }
  }, [selectedTrainingForNotes, notesData]);

  return (
    <div className="min-h-screen w-screen pb-20" style={{ background: '#160C0C' }}>
      <TopNavigation variant="subtle" />
      
      {/* Instructor Tabs */}
      {isInstructor && (
        <div className="pt-24 px-5">
          <div className="flex border-b-2 border-white/20 mb-4">
            <button
              data-testid="my-classes-tab"
              onClick={() => handleTabChange('classes')}
              className={`flex-1 pb-2 px-1 text-xs font-semibold ${
                activeTab === 'classes' 
                  ? 'text-white border-b-2 border-sossiety-red' 
                  : 'text-white/60 border-b-2 border-transparent'
              }`}
            >
              {t('calendar.my_classes')}
            </button>
            <button
              data-testid="my-training-sessions-tab"
              onClick={() => handleTabChange('trainings')}
              className={`flex-1 pb-2 px-1 text-xs font-semibold ${
                activeTab === 'trainings' 
                  ? 'text-white border-b-2 border-sossiety-red' 
                  : 'text-white/60 border-b-2 border-transparent'
              }`}
            >
              {t('calendar.my_trainings')}
            </button>
            <button
              data-testid="my-notes-tab"
              onClick={() => handleTabChange('notes')}
              className={`flex-1 pb-2 px-1 text-xs font-semibold ${
                activeTab === 'notes' 
                  ? 'text-white border-b-2 border-sossiety-red' 
                  : 'text-white/60 border-b-2 border-transparent'
              }`}
            >
              {t('calendar.my_notes_tab')}
            </button>
          </div>
        </div>
      )}
      
      {/* Non-Instructor Tabs (only if not instructor) */}
      {!isInstructor && (
        <div className="pt-24 px-5">
          <div className="flex border-b-2 border-white/20 mb-4">
            <button
              data-testid="my-training-sessions-tab"
              onClick={() => handleTabChange('trainings')}
              className={`flex-1 pb-2 px-1 text-xs font-semibold ${
                activeTab === 'trainings' 
                  ? 'text-white border-b-2 border-sossiety-red' 
                  : 'text-white/60 border-b-2 border-transparent'
              }`}
            >
              {t('calendar.my_trainings')}
            </button>
            <button
              data-testid="my-notes-tab"
              onClick={() => handleTabChange('notes')}
              className={`flex-1 pb-2 px-1 text-xs font-semibold ${
                activeTab === 'notes' 
                  ? 'text-white border-b-2 border-sossiety-red' 
                  : 'text-white/60 border-b-2 border-transparent'
              }`}
            >
              {t('calendar.my_notes_tab')}
            </button>
          </div>
        </div>
      )}
      
      {/* Fixed Action Bar with Month Navigation - Only show for trainings tab */}
      {activeTab !== 'notes' && (
        <div 
          className={`fixed top-20 left-0 right-0 z-40 px-5 transition-all duration-300 ${
            showFixedButton 
              ? 'translate-y-0 opacity-100' 
              : '-translate-y-full opacity-0 pointer-events-none'
          }`}
        >
          <div className="max-w-md mx-auto space-y-3">
            {/* Month Navigation */}
            <div className="flex items-center justify-between bg-[#281B1B]/90 backdrop-blur-sm rounded-lg p-3 border border-white/20">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleMonthChange('prev')}
                className="text-white hover:bg-white/10"
              >
                <ArrowLeft className="w-4 h-4" />
              </Button>
              <h2 className="text-sm font-semibold text-white">
                {(() => {
                  const month = format(selectedDate, 'MMMM', { locale: currentLanguage === 'pt' ? ptBR : undefined });
                  const capitalizedMonth = month.charAt(0).toUpperCase() + month.slice(1);
                  return t('calendar.month_year', { 
                    month: capitalizedMonth, 
                    year: format(selectedDate, 'yyyy') 
                  });
                })()}
              </h2>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleMonthChange('next')}
                className="text-white hover:bg-white/10"
              >
                <ArrowRight className="w-4 h-4" />
              </Button>
            </div>
            
            {/* Add Training Button - Only show for trainings tab */}
            {activeTab === 'trainings' && (
              <Button
                variant="outline"
                size="lg"
                onClick={() => setIsAddTrainingModalOpen(true)}
                className="w-full bg-[#281B1B]/90 backdrop-blur-sm"
              >
                <Plus className="w-4 h-4" />
                {t('calendar.add_training')}
              </Button>
            )}
          </div>
        </div>
      )}
      
      <main className="px-5">
        <div className={`${activeTab === 'notes' ? 'pt-0 pb-6' : 'pt-2 pb-6'}`}>
          {/* Conditional Content based on Tab */}
          {activeTab === 'notes' ? (
            // My Notes Tab
            <MyNotesTab
              selectedDate={selectedDate}
              onDateChange={setSelectedDate}
            />
          ) : (
            // Regular monthly card list — used for all non-notes tabs (classes, trainings, student)
            <>
              {/* Action Bar - Show for classes and trainings tabs */}
              {(activeTab === 'trainings' || isClassesTab) && (
                <div ref={actionBarRef} className="flex items-center gap-3 mb-6">
                  {isClassesTab ? (
                    <Button
                      variant="outline"
                      size="lg"
                      onClick={() => setIsAddClassModalOpen(true)}
                      className="flex-1"
                    >
                      <Plus className="w-4 h-4" />
                      {t('calendar.add_class')}
                    </Button>
                  ) : (
                    <>
                      <Button
                        variant="outline"
                        onClick={() => setIsAddTrainingModalOpen(true)}
                        className="flex-1 border-white text-white hover:bg-white/10 rounded-full font-semibold bg-transparent"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        {t('calendar.add_training')}
                      </Button>
                      <button
                        onClick={handleOpenFilterModal}
                        className={`flex items-center justify-center p-2 rounded-md transition-colors ${
                          hasActiveFilters()
                            ? 'text-white bg-white/10'
                            : 'text-white hover:bg-white/10'
                        }`}
                      >
                        <Filter className="w-4 h-4" />
                        {hasActiveFilters() && <span className="ml-0.5 text-xs font-bold">•</span>}
                      </button>
                    </>
                  )}
                </div>
              )}

              {/* Month Slider - Show for trainings tab and classes tab (for instructors) */}
              {/* monthlyData is always 36 items (12 months x 3 years), so we can safely check for its existence */}
              {((activeTab === 'trainings') || (isInstructor && activeTab === 'classes')) && !instructorLoading && monthlyData && monthlyData.length === 36 && (
                <div data-testid="calendar-month-slider-wrapper">
                  <MonthSlider
                    selectedMonth={selectedMonth}
                    selectedYear={selectedYear}
                    onMonthSelect={(month, year) => {
                      // month é 1-indexed, mas setDate espera 0-indexed
                      setSelectedDate(new Date(year, month - 1, 1));
                    }}
                    monthlyTotals={monthlyData}
                  />
                </div>
              )}

              {/* Monthly Navigation - Show for trainings and classes tabs */}
              {(activeTab === 'trainings' || isClassesTab) && (
                <div ref={monthNavigationRef} className="flex items-center justify-between mb-3">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleMonthChange('prev')}
                    className="text-white hover:bg-white/10"
                  >
                    <ArrowLeft className="w-5 h-5" />
                  </Button>
                  <h2 className="text-lg font-semibold text-white">
                    {(() => {
                      const month = format(selectedDate, 'MMMM', { locale: currentLanguage === 'pt' ? ptBR : undefined });
                      const capitalizedMonth = month.charAt(0).toUpperCase() + month.slice(1);
                      return t('calendar.month_year', { 
                        month: capitalizedMonth, 
                        year: format(selectedDate, 'yyyy') 
                      });
                    })()}
                  </h2>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleMonthChange('next')}
                    className="text-white hover:bg-white/10"
                  >
                    <ArrowRight className="w-5 h-5" />
                  </Button>
                </div>
              )}

              {/* Filter Status */}
              {hasActiveFilters() && (
                <div className="mb-6 p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-white">
                      {t('calendar.showing_trainings', { filtered: filteredTrainings.length, total: trainings.length })}
                    </span>
                    <button 
                      onClick={handleClearFilters}
                      className="text-white hover:text-white/80 text-xs font-medium underline"
                    >
                      {t('calendar.clear_filters')}
                    </button>
                  </div>
                </div>
              )}

              {/* Content */}

              {(trainingsLoading || (isClassesTab && instructorClassesLoading)) ? (
                <div className="flex items-center justify-center py-8">
                  <div className="text-lg text-white">{t('loading.trainings')}</div>
                </div>
              ) : filteredTrainings.length === 0 ? (
                <div className="flex flex-col items-center justify-center min-h-[60vh] text-center px-4">
                  {/* Empty State Icon */}
                  <div className="w-20 h-20 bg-[#281B1B] rounded-full flex items-center justify-center mb-6">
                    <svg 
                      className="w-10 h-10 text-white/40" 
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24"
                    >
                      <path 
                        strokeLinecap="round" 
                        strokeLinejoin="round" 
                        strokeWidth={1.5} 
                        d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" 
                      />
                    </svg>
                  </div>
                  
                  {/* Empty State Title */}
                  <h3 className="text-xl font-semibold text-white mb-3 font-roboto">
                    {isInstructor && activeTab === 'classes' ? t('calendar.start_teaching_add_first_class') : t('calendar.no_classes_scheduled_today')}
                  </h3>
                  
                  {/* Empty State Subtitle */}
                  <p className="text-white/60 text-sm max-w-sm leading-relaxed font-roboto">
                    {isInstructor && activeTab === 'classes' ? t('calendar.schedule_sessions_help_students') : t('calendar.start_teaching_add_first_class')}
                  </p>
                  
                  {/* Call to Action Button */}
                  <Button
                    variant="outline"
                    size="lg"
                    onClick={() => isInstructor && activeTab === 'classes' ? setIsAddClassModalOpen(true) : setIsAddTrainingModalOpen(true)}
                    className="mt-8"
                  >
                    <Plus className="w-4 h-4" />
                    {isInstructor && activeTab === 'classes' ? t('calendar.add_your_first_class') : t('calendar.add_training')}
                  </Button>
                </div>
              ) : (
                <div className="space-y-6">
                  {filteredTrainings.length > 0 ? (
                    <div className="space-y-6">
                      {filteredTrainings.map((training) => {
                        const trainingDate = new Date(training.start_time);
                        const dayString = format(trainingDate, 'yyyy-MM-dd');
                        const isCurrent = isCurrentDay(trainingDate);
                        
                        return (
                          <div 
                            key={training.id} 
                            ref={isCurrent ? currentDayRef : null}
                            className={`${isCurrent ? 'ring-2 ring-sossiety-red rounded-lg p-2' : ''}`}
                          >
                            <div className="flex gap-2">
                              {/* Date Column */}
                              <div className="flex-shrink-0 w-12 text-center">
                                <div className={`text-sm font-semibold ${
                                  isCurrent ? 'text-sossiety-red' : 'text-white'
                                }`}>
                                  {(() => {
                                    const dayName = format(trainingDate, 'EEE', { locale: currentLanguage === 'pt' ? ptBR : undefined });
                                    const abbreviated = dayName.substring(0, 3);
                                    return abbreviated.charAt(0).toUpperCase() + abbreviated.slice(1);
                                  })()}
                                </div>
                                <div className={`text-lg font-semibold ${
                                  isCurrent ? 'text-sossiety-red' : 'text-white'
                                }`}>
                                  {format(trainingDate, 'dd')}
                                </div>
                              </div>
                              
                              {/* Training Card */}
                              <div className="flex-1">
                                <TrainingCard
                                  ref={(ref) => {
                                    if (ref) {
                                      trainingCardRefs.current.set(training.id, ref);
                                    } else {
                                      trainingCardRefs.current.delete(training.id);
                                    }
                                  }}
                                  training={training}
                                  notesData={notesData}
                                  onStatusUpdate={handleStatusUpdate}
                                  onNotesUpdate={handleNotesUpdate}
                                  isCurrentDay={isCurrent}
                                  onViewTraining={handleOpenViewModal}
                                  notesRefreshKey={notesRefreshKey}
                                  isUpdatingNotes={updatingNotesTrainingId === training.id}
                                />
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-center text-white/60 py-8">
                      {t('calendar.no_classes_scheduled_today')}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </main>
      
      <BottomNavigation />

      {/* Add/Edit Training Modal (only for non-instructor or trainings tab) */}
      <AddTrainingModal
        isOpen={(isAddTrainingModalOpen || isEditModalOpen) && (!isInstructor || activeTab === 'trainings')}
        onClose={() => {
          console.log('🔍 [Calendar] AddTrainingModal onClose called');
          // Close modal immediately
          setIsAddTrainingModalOpen(false);
          setIsEditModalOpen(false);
          setSelectedTrainingForEdit(null);
        }}
        onTrainingCreated={handleTrainingCreated}
        selectedDate={selectedDate}
        onOpenNotesModal={handleOpenNotesModal}
        mode={isEditModalOpen ? 'edit' : 'create'}
        trainingToEdit={selectedTrainingForEdit}
        onTrainingUpdated={handleTrainingUpdated}
      />

      {/* Add/Edit Class Modal */}
      <AddClassModal
        isOpen={(isAddClassModalOpen || isEditClassModalOpen) && (isInstructor && activeTab === 'classes')}
        onClose={() => {
          setIsAddClassModalOpen(false);
          setIsEditClassModalOpen(false);
          setSelectedClassForEdit(null);
        }}
        onClassCreated={handleClassCreated}
        mode={isEditClassModalOpen ? 'edit' : 'create'}
        classToEdit={selectedClassForEdit}
        onClassUpdated={handleClassUpdated}
      />

      {/* Training Notes Modal */}
      <TrainingNotesModal
        isOpen={isNotesModalOpen}
        onClose={() => {
          setIsNotesModalOpen(false);
          setSelectedTrainingForNotes(null);
          setUpdatingNotesTrainingId(null); // Reset loading state when modal is closed
        }}
        training={selectedTrainingForNotes}
        notesData={notesData}
        onSave={handleNotesSaved}
        onDataSaved={handleDataSaved}
      />

      {/* Training View Modal */}
      {/* Only render modal if training is available AND modal should be open to prevent overlay issues */}
      {selectedTrainingForView && (
        <TrainingViewModal
          isOpen={isViewModalOpen && !!selectedTrainingForView}
          onClose={() => {
            setIsViewModalOpen(false);
            // Clean up notification URL/state only after the user closes the modal
            if (fromNotificationRef.current) {
              window.history.replaceState({}, document.title);
              fromNotificationRef.current = false;
            }
            // Clear training after a small delay to ensure Dialog closes properly
            setTimeout(() => {
              setSelectedTrainingForView(null);
              
              // Force refetch of data to ensure monthlyData is recalculated
              // Use await to ensure refetch completes before invalidating
              Promise.all([
                refetchTrainings(),
                isInstructor ? refetchInstructorClasses() : Promise.resolve()
              ]).then(() => {
                // Invalidate queries after refetch completes
                queryClient.invalidateQueries({ queryKey: ['trainings-with-notes'] });
                queryClient.invalidateQueries({ queryKey: ['instructor-classes'] });
                queryClient.invalidateQueries({ queryKey: ['instructor-classes-for-month'], exact: false });
              });
              
              // Ensure scroll is at top after closing modal
              requestAnimationFrame(() => {
                window.scrollTo(0, 0);
                setTimeout(() => {
                  window.dispatchEvent(new Event('scroll'));
                }, 50);
              });
            }, 100);
          }}
          training={selectedTrainingForView}
          notesData={notesData}
          onStatusUpdate={handleStatusUpdate}
          onOpenNotesModal={handleOpenNotesModal}
          onEditTraining={handleOpenEditModal}
          onTrainingDeleted={handleTrainingDeleted}
          onTrainingUpdated={handleTrainingUpdated}
          refreshKey={viewModalRefreshKey}
          ref={viewModalRef}
        />
      )}

      {/* Delete Class Modal */}
      <DeleteClassModal
        isOpen={isDeleteClassModalOpen}
        onClose={() => {
          setIsDeleteClassModalOpen(false);
          setSelectedClassForDelete(null);
        }}
        onConfirm={handleConfirmDeleteClass}
      />

      {/* Training Filter Modal */}
      <TrainingFilterModal
        isOpen={isFilterModalOpen}
        onClose={() => setIsFilterModalOpen(false)}
        onApplyFilters={handleApplyFilters}
        onClearFilters={handleClearFilters}
        currentFilters={activeFilters}
        hasActiveFilters={hasActiveFilters()}
      />
    </div>
  );
} 